import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Plus, 
  FileText, 
  Trash2, 
  BarChart3, 
  Clock, 
  ChevronRight,
  Zap,
  Rocket,
  AlertTriangle,
  TrendingUp
} from "lucide-react";
import Navbar from "../components/Navbar";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const fetchComparisons = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/comparisons`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setComparisons(response.data);
    } catch (error) {
      console.error("Error fetching comparisons:", error);
      toast.error("Failed to load comparisons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComparisons();
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API}/comparisons/${deleteId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      toast.success("Comparison deleted");
      fetchComparisons();
    } catch (error) {
      toast.error("Failed to delete comparison");
    } finally {
      setDeleteId(null);
    }
  };

  const handleNewComparison = () => {
    const tierLimits = { free: 3, standard: 20, pro: Infinity };
    const limit = tierLimits[user?.tier || "free"];
    
    if ((user?.comparisons_used || 0) >= limit) {
      setShowUpgradeModal(true);
    } else {
      navigate("/comparison/new");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "text-accent-success bg-accent-success/10";
      case "compared": return "text-primary-main bg-primary-main/10";
      default: return "text-gray-400 bg-gray-500/10";
    }
  };

  const tierLimits = {
    free: 3,
    standard: 20,
    pro: Infinity
  };

  const tierFeatures = {
    free: ["3 comparisons/month", "3 vendors max", "Basic matching"],
    standard: ["20 comparisons/month", "10 vendors max", "AI analysis", "Excel export"],
    pro: ["Unlimited comparisons", "Unlimited vendors", "Advanced AI", "Priority support"]
  };

  const limit = tierLimits[user?.tier || "free"];
  const usagePercent = Math.min(((user?.comparisons_used || 0) / limit) * 100, 100);
  const isNearLimit = usagePercent >= 80;
  const isAtLimit = (user?.comparisons_used || 0) >= limit;

  return (
    <div className="min-h-screen bg-[#020408]">
      <Navbar user={user} />

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
            <div>
              <h1 className="font-display text-4xl font-bold tracking-tight mb-2">
                Welcome, <span className="text-primary-main">{user?.name?.split(' ')[0] || 'User'}</span>
              </h1>
              <p className="text-gray-400">
                Manage your vendor comparisons and start new analyses
              </p>
            </div>
            
            <Button
              data-testid="new-comparison-btn"
              onClick={handleNewComparison}
              className="btn-skew bg-primary-main text-black font-bold uppercase tracking-wider px-6 py-5 hover:bg-primary-light hover:shadow-glow-cyan transition-all"
            >
              <span className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                New Comparison
              </span>
            </Button>
          </div>

          {/* Upgrade Banner for Free Users */}
          {user?.tier === "free" && (
            <div className={`mb-8 p-6 rounded-xl border ${isNearLimit ? "bg-amber-500/5 border-amber-500/30" : "bg-primary-main/5 border-primary-main/30"}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  {isNearLimit ? (
                    <AlertTriangle className="w-8 h-8 text-amber-500 flex-shrink-0" />
                  ) : (
                    <Rocket className="w-8 h-8 text-primary-main flex-shrink-0" />
                  )}
                  <div>
                    <h3 className="font-display text-lg font-semibold mb-1">
                      {isAtLimit 
                        ? "You've reached your free tier limit" 
                        : isNearLimit 
                        ? `Only ${limit - (user?.comparisons_used || 0)} comparison(s) left` 
                        : "Unlock the full power of AI analysis"
                      }
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {isAtLimit 
                        ? "Upgrade to Standard or Pro to continue creating comparisons and unlock AI-powered insights."
                        : "Upgrade to get more comparisons, advanced AI analysis, and Excel exports."
                      }
                    </p>
                  </div>
                </div>
                <Button
                  data-testid="upgrade-banner-btn"
                  onClick={() => setShowUpgradeModal(true)}
                  className={`flex-shrink-0 ${isNearLimit ? "bg-amber-500 hover:bg-amber-400" : "bg-primary-main hover:bg-primary-light"} text-black font-bold uppercase tracking-wider px-6`}
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Upgrade Now
                </Button>
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Usage Card */}
            <div className="glass rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400 text-sm uppercase tracking-wider">Usage</span>
                <span className={`px-2 py-1 text-xs font-mono uppercase rounded ${
                  user?.tier === "pro" ? "bg-secondary-main/10 text-secondary-main" :
                  user?.tier === "standard" ? "bg-primary-main/10 text-primary-main" :
                  "bg-gray-500/10 text-gray-400"
                }`}>
                  {user?.tier || "free"}
                </span>
              </div>
              <div className="mb-3">
                <span className="font-display text-3xl font-bold">
                  {user?.comparisons_used || 0}
                </span>
                <span className="text-gray-500 text-lg">
                  {" / "}{limit === Infinity ? "∞" : limit}
                </span>
              </div>
              <div className="h-2 bg-background-subtle rounded-full overflow-hidden mb-3">
                <div 
                  className={`h-full transition-all duration-500 ${
                    isAtLimit ? "bg-red-500" : isNearLimit ? "bg-amber-500" : "bg-primary-main"
                  }`}
                  style={{ width: `${usagePercent}%` }}
                />
              </div>
              {user?.tier === "free" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUpgradeModal(true)}
                  className="w-full text-primary-main hover:bg-primary-main/10 mt-2"
                  data-testid="usage-upgrade-btn"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  Upgrade for more
                </Button>
              )}
            </div>

            {/* Total Comparisons */}
            <div className="glass rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400 text-sm uppercase tracking-wider">Total</span>
                <BarChart3 className="w-5 h-5 text-secondary-main" />
              </div>
              <span className="font-display text-3xl font-bold">
                {comparisons.length}
              </span>
              <p className="text-gray-500 text-sm mt-1">Comparisons created</p>
            </div>

            {/* Quick Action */}
            <div 
              className="glass rounded-xl p-6 cursor-pointer hover:border-primary-main/50 transition-all group"
              onClick={() => navigate("/comparison/new")}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400 text-sm uppercase tracking-wider">Quick Start</span>
                <Zap className="w-5 h-5 text-accent-warning" />
              </div>
              <p className="font-display text-xl font-semibold group-hover:text-primary-main transition-colors">
                Upload vendor quotes to start comparing
              </p>
              <ChevronRight className="w-5 h-5 text-gray-500 mt-2 group-hover:text-primary-main group-hover:translate-x-1 transition-all" />
            </div>
          </div>

          {/* Comparisons List */}
          <div className="space-y-4">
            <h2 className="font-display text-2xl font-semibold mb-6">Your Comparisons</h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-pulse-glow w-12 h-12 rounded-full border-2 border-primary-main" />
              </div>
            ) : comparisons.length === 0 ? (
              <div className="glass rounded-xl p-12 text-center">
                <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="font-display text-xl font-semibold mb-2">No comparisons yet</h3>
                <p className="text-gray-400 mb-6">Create your first vendor comparison to get started</p>
                <Button
                  data-testid="empty-new-comparison-btn"
                  onClick={handleNewComparison}
                  className="btn-skew bg-primary-main text-black font-bold uppercase tracking-wider px-6 py-4 hover:bg-primary-light transition-all"
                >
                  <span className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Create Comparison
                  </span>
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {comparisons.map((comparison) => (
                  <div
                    key={comparison.comparison_id}
                    data-testid={`comparison-card-${comparison.comparison_id}`}
                    className="glass rounded-xl p-6 hover:border-primary-main/30 transition-all cursor-pointer group"
                    onClick={() => navigate(`/comparison/${comparison.comparison_id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-display text-xl font-semibold group-hover:text-primary-main transition-colors">
                            {comparison.name}
                          </h3>
                          <span className={`px-2 py-0.5 text-xs font-mono uppercase rounded ${getStatusColor(comparison.status)}`}>
                            {comparison.status}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm mb-3">
                          {comparison.description || "No description"}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {comparison.vendors?.length || 0} vendors
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(comparison.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-comparison-${comparison.comparison_id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(comparison.comparison_id);
                          }}
                          className="text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                        <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-primary-main group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-background-paper border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete Comparison?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This action cannot be undone. This will permanently delete the comparison and all associated vendor data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border hover:bg-background-subtle">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-background-paper border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Upgrade Your Plan</DialogTitle>
            <DialogDescription className="text-gray-400">
              Unlock more comparisons and powerful AI features
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-4 py-4">
            {/* Standard Plan */}
            <div className="glass rounded-xl p-6 border border-primary-main/50 hover:border-primary-main transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary-main/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-primary-main" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold">Standard</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-primary-main">$29</span>
                    <span className="text-gray-500 text-sm">/month</span>
                  </div>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6">
                {tierFeatures.standard.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                    <Zap className="w-4 h-4 text-primary-main" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Button
                data-testid="upgrade-standard-btn"
                onClick={() => {
                  toast.success("Redirecting to checkout...");
                  // In production, this would redirect to Stripe checkout
                  setShowUpgradeModal(false);
                }}
                className="w-full bg-primary-main text-black font-bold hover:bg-primary-light"
              >
                Upgrade to Standard
              </Button>
            </div>
            
            {/* Pro Plan */}
            <div className="glass rounded-xl p-6 border border-secondary-main/50 hover:border-secondary-main transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-secondary-main/20 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-secondary-main" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold">Pro</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-secondary-main">$79</span>
                    <span className="text-gray-500 text-sm">/month</span>
                  </div>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6">
                {tierFeatures.pro.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                    <Rocket className="w-4 h-4 text-secondary-main" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Button
                data-testid="upgrade-pro-btn"
                onClick={() => {
                  toast.success("Redirecting to checkout...");
                  // In production, this would redirect to Stripe checkout
                  setShowUpgradeModal(false);
                }}
                className="w-full bg-secondary-main text-black font-bold hover:bg-secondary-light"
              >
                Upgrade to Pro
              </Button>
            </div>
          </div>
          
          <p className="text-center text-xs text-gray-500">
            Cancel anytime. No questions asked.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
